<template>
  <div
    id="app"
    class="h-screen w-screen overflow-hidden"
    :class="{
      'not-select-display slide-cursor': frameStatePresenter.isHandlingSlideBar.value,
      'panel-cursor-area': editorPageStatePresenter.isHandlingTestFrame,
      'text-gray-800': configStatePresenter.isLightMode(),
      'darkScrollBar dark bg-dark-900 text-dark-text border-white': !configStatePresenter.isLightMode()
    }"
    :style="`max-height: ${configStatePresenter.windowHeight.value}px`"
  >
    <div>
      <AppLayout>
        <PrimaryHeader class="header-area select step1" />
        <SideMenuLeft class="sidemenu-area-left border-r border-primary-100 dark:border-dark-400" />
        <SideMenuRight class="sidemenu-area-right border-l border-gray-300 dark:border-dark-400" />
        <div
          class="scrollbar slide-cursor hover:bg-primary"
          :class="{
            'bg-primary': frameStatePresenter.isHandlingSlideBar.value
          }"
          @mousedown="frameStatePresenter.mouseDownSlideBar"
          @mousemove="event => frameStatePresenter.resizeLeftSideFrameWidth(event as MouseEvent)"
        />
        <div
          id="problem"
          ref="leftSideFrameElement"
          class="problem-area border-r flex overflow-y-scroll border-primary-100 dark:border-dark-200"
          :class="{
            'custom-scrollbar': configStatePresenter.isLightMode(),
            'custom-scrollbar-dark': !configStatePresenter.isLightMode()
          }"
          :style="leftAreaStyle"
        >
          <div class="w-full h-full">
            <ProblemDescriptionPage
              v-if="frameStatePresenter.leftSideFrame.value == 'Problem'"
              class="py-2 px-4 w-auto"
              :scroll="scrollTopLeftPanel"
            />
            <ProblemStatisticsPage
              v-if="frameStatePresenter.leftSideFrame.value == 'Statistics'"
              class="py-2 px-4 w-auto"
            />
            <TopicCommentaryPage
              v-if="frameStatePresenter.leftSideFrame.value == 'TopicCommentary'"
              class="py-2 px-4 w-auto"
              :idNumber="1"
            />
            <PreCommentaryPage v-if="frameStatePresenter.leftSideFrame.value == 'PreCommentary'" class="py-5 w-auto" />
            <PostCommentaryPage
              v-if="frameStatePresenter.leftSideFrame.value == 'PostCommentary'"
              class="py-5 w-auto"
            />
            <HistoryPage v-if="frameStatePresenter.leftSideFrame.value == 'History'" />
            <ModelAnswerPage v-if="frameStatePresenter.leftSideFrame.value == 'ModelAnswer'" />
            <ReviewViewer v-if="frameStatePresenter.leftSideFrame.value == 'Review'" />
          </div>
        </div>
        <div
          ref="rightSideFrameElement"
          class="editor-area h-full border-l relative min-w-0 border-primary-100 dark:border-dark-200"
          :class="{
            'overflow-y-auto': frameStatePresenter.rightSideFrame.value != 'Editor',
            'custom-scrollbar': configStatePresenter.isLightMode(),
            'custom-scrollbar-dark': !configStatePresenter.isLightMode()
          }"
        >
          <div class="h-full w-full">
            <EditorPage
              v-if="frameStatePresenter.rightSideFrame.value == 'Editor'"
              :key="0"
              :keySubmmit="keySubmmit"
              :keyTest="keyTest"
              :shortcutKey="nested.shortcutKey"
              :sideMenuWidth="frameStatePresenter.leftSideFrameWidth"
            />

            <JudgeDetailPage
              v-if="frameStatePresenter.rightSideFrame.value == 'JudgeDetail'"
              :key="1"
              class="w-full h-full"
            />
            <ReviewPostViewer
              v-if="frameStatePresenter.rightSideFrame.value == 'ReviewPost'"
              @scroll="scrollTopRightPanel"
            />
            <JudgeQueueViewer
              v-if="frameStatePresenter.rightSideFrame.value == 'JudgeQueue'"
              :key="2"
              class="w-full h-full"
            />
          </div>
        </div>
        <LoginModal
          v-if="modalStatePresenter.isShowLoginModal.value && !authStatePresenter.isLogin.value"
          :login="authStatePresenter.login"
        />
        <ArenaModal v-if="modalStatePresenter.isShowArenaModal.value" />
        <SettingsModal v-if="modalStatePresenter.isShowSettingsModal.value" :shortcutKey="nested.shortcutKey" />
        <UpdateHistoryModal v-if="modalStatePresenter.isShowUpdateModal.value" />
        <ReviewRequestModal v-if="modalStatePresenter.isShowReviewAlertModal.value" />
        <DashboardModal v-if="modalStatePresenter.isShowDashboardModal.value" />
        <ProblemSearchModal v-if="modalStatePresenter.isShowProblemSearchModal.value" />
        <FreeSpaceModal v-if="modalStatePresenter.isShowFreeSpaceModal.value" />
        <UserRegistrationModal v-if="modalStatePresenter.isShowUserRegistrationModal.value" />
      </AppLayout>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onBeforeMount, onMounted, onUnmounted, ref, Ref, SetupContext, watch } from 'vue'

import 'shepherd.js/dist/css/shepherd.css'

import ArenaModal from '@/presentation/components/Arena/ArenaModal.vue'
import PostCommentaryPage from '@/presentation/components/Commentary/PostCommentaryPage.vue'
import PreCommentaryPage from '@/presentation/components/Commentary/PreCommentaryPage.vue'
import TopicCommentaryPage from '@/presentation/components/Commentary/TopicCommentaryPage.vue'
import DashboardModal from '@/presentation/components/Dashboard/DashboardModal.vue'
import EditorPage from '@/presentation/components/Editor/EditorPage.vue'
import AppLayout from '@/presentation/components/Frame/AppLayout.vue'
import FreeSpaceModal from '@/presentation/components/FreeSpace/FreeSpaceModal.vue'
import PrimaryHeader from '@/presentation/components/Header/PrimaryHeader.vue'
import HistoryPage from '@/presentation/components/History/HistoryPage.vue'
import JudgeDetailPage from '@/presentation/components/JudgeDetail/JudgeDetailPage.vue'
import JudgeQueueViewer from '@/presentation/components/JudgeQueue/JudgeQueueViewer.vue'
import LoginModal from '@/presentation/components/Login/LoginModal.vue'
import ModelAnswerPage from '@/presentation/components/ModelAnswer/ModelAnswerPage.vue'
import ProblemDescriptionPage from '@/presentation/components/ProblemDescription/ProblemDescriptionPage.vue'
import ProblemSearchModal from '@/presentation/components/ProblemSearch/ProblemSearchModal.vue'
import ProblemStatisticsPage from '@/presentation/components/ProblemStatistics/ProblemStatisticsPage.vue'
import ReviewViewer from '@/presentation/components/Review/ReviewViewer.vue'
import ReviewPostViewer from '@/presentation/components/ReviewPost/ReviewPostViewer.vue'
import ReviewRequestModal from '@/presentation/components/ReviewRequest/ReviewRequestModal.vue'
import SettingsModal from '@/presentation/components/Settings/SettingsModal.vue'
import SideMenuLeft from '@/presentation/components/SideMenu/SideMenuLeft.vue'
import SideMenuRight from '@/presentation/components/SideMenu/SideMenuRight.vue'
import UpdateHistoryModal from '@/presentation/components/UpdateHistory/UpdateHistoryModal.vue'
import UserRegistrationModal from '@/presentation/components/UserRegistration/UserRegistrationModal.vue'
import { useArenaPresenter } from '@/presentation/composables/arena/useArenaPresenter'
import { usePostArenaSubmissionPresenter } from '@/presentation/composables/arena/usePostArenaSubmissionPresenter'
import { useAuthStatePresenter } from '@/presentation/composables/auth/useAuthStatePresenter'
import { useBookmarkPresenter } from '@/presentation/composables/bookmark/useBookmarkPresenter'
import { useConfigStatePresenter } from '@/presentation/composables/config/useConfigStatePresenter'
import { useDashboardModalStatePresenter } from '@/presentation/composables/dashboard/useDashboardModalStatePresenter'
import { useEditorPageStatePresenter } from '@/presentation/composables/editor/useEditorPageStatePresenter'
import { useFrameStatePresenter } from '@/presentation/composables/frame/useFrameStatePresenter'
import { useHistoryPageStatePresenter } from '@/presentation/composables/history/useHistoryPageStatePresenter'
import { useHistoryPresenter } from '@/presentation/composables/history/useHistoryPresenter'
import { useJudgeDetailPresenter } from '@/presentation/composables/judge/useJudgeDetailPresenter'
import { useJudgeStatePresenter } from '@/presentation/composables/judge/useJudgeStatePresenter'
import { usePostSubmissionPresenter } from '@/presentation/composables/judge/usePostSubmissionPresenter'
import { usePostTestSubmissionPresenter } from '@/presentation/composables/judge/usePostTestSubmissionPresenter'
import { useRecentJudgeListPresenter } from '@/presentation/composables/judge/useRecentJudgeListPresenter'
import { useModalStatePresenter } from '@/presentation/composables/modal/useModalStatePresenter'
import { useModelAnswerPageStatePresenter } from '@/presentation/composables/modelAnswer/useModelAnswerPageStatePresenter'
import { useModelAnswerPresenter } from '@/presentation/composables/modelAnswer/useModelAnswerPresenter'
import { useNotificationPresenter } from '@/presentation/composables/notification/useNotificationPresenter'
import { useChallengePresenter } from '@/presentation/composables/problem/useChallengePresenter'
import { useCoursePresenter } from '@/presentation/composables/problem/useCoursePresenter'
import { useProblemDescriptionPresenter } from '@/presentation/composables/problem/useProblemDescriptionPresenter'
import { useProblemStatePresenter } from '@/presentation/composables/problem/useProblemStatePresenter'
import { useProblemStatisticsPresenter } from '@/presentation/composables/problem/useProblemStatisticsPresenter'
import { useReviewPageStatePresenter } from '@/presentation/composables/review/useReviewPageStatePresenter'
import { useReviewPostPageStatePresenter } from '@/presentation/composables/reviewPost/useReviewPageStatePresenter'
import { useJudgeCasePresenter } from '@/presentation/composables/testCase/useJudgeCasePresenter'
import { useTestCasePresenter } from '@/presentation/composables/testCase/useTestCasePresenter'
import { useConfettiPresenter } from '@/presentation/composables/util/useConfettiPresenter'
import { useIntroPresenter } from '@/presentation/composables/util/useIntroPresenter'

export default defineComponent({
  name: 'app',
  components: {
    AppLayout,
    PrimaryHeader,
    SideMenuLeft,
    SideMenuRight,
    LoginModal,
    DashboardModal,
    SettingsModal,
    UpdateHistoryModal,
    ArenaModal,
    ReviewRequestModal,
    ProblemSearchModal,
    UserRegistrationModal,
    FreeSpaceModal,
    ProblemDescriptionPage,
    HistoryPage,
    ModelAnswerPage,
    EditorPage,
    JudgeQueueViewer,
    PreCommentaryPage,
    PostCommentaryPage,
    ProblemStatisticsPage,
    JudgeDetailPage,
    ReviewViewer,
    ReviewPostViewer,
    TopicCommentaryPage
  },
  setup(_, ctx: SetupContext) {
    const Super = ref<HTMLElement>()
    const authStatePresenter = useAuthStatePresenter()
    const configStatePresenter = useConfigStatePresenter()
    const coursePresenter = useCoursePresenter()
    const challengePresenter = useChallengePresenter()
    const problemStatisticsPresenter = useProblemStatisticsPresenter()
    const reviewPostPageStatePresenter = useReviewPostPageStatePresenter()
    const frameStatePresenter = useFrameStatePresenter()
    const modalStatePresenter = useModalStatePresenter()
    const modelAnswerPageStatePresenter = useModelAnswerPageStatePresenter()
    const historyPageStatePresenter = useHistoryPageStatePresenter()
    const editorPageStatePresenter = useEditorPageStatePresenter()
    const introPresenter = useIntroPresenter()
    const notificationPresenter = useNotificationPresenter()
    const arenaPresenter = useArenaPresenter()
    const bookmarkPresenter = useBookmarkPresenter()
    const dashboardModalStatePresenter = useDashboardModalStatePresenter()
    const { getProblemDescription } = useProblemDescriptionPresenter()
    const { getTestCaseList } = useTestCasePresenter()
    const { getJudgeCaseList } = useJudgeCasePresenter()
    const { changeProblem, problemId, setProblemId } = useProblemStatePresenter()
    const { addModelAnswerList } = useModelAnswerPresenter()
    const { addHistoryList, initState } = useHistoryPresenter()
    const { isJudgeLoading } = useJudgeStatePresenter()
    const { judgeDetail } = useJudgeDetailPresenter()
    const { runConfetti } = useConfettiPresenter()
    const { setIsFirstOpendPage: setReviewIsFirstOpendPage } = useReviewPageStatePresenter()
    const { postSubmission } = usePostSubmissionPresenter()
    const { postTestSubmission } = usePostTestSubmissionPresenter()
    const { postArenaSubmission } = usePostArenaSubmissionPresenter()
    const { getRecentJudgeResultList } = useRecentJudgeListPresenter()

    const initAppState = () => {
      coursePresenter.getCourseList(configStatePresenter.displayLanguage.value)
      challengePresenter.getChallengeList(configStatePresenter.displayLanguage.value)

      getProblemDescription(configStatePresenter.displayLanguage.value, 'ITP1_1_A')
      getTestCaseList('ITP1_1_A')
      getJudgeCaseList('ITP1_1_A')
      problemStatisticsPresenter.getProblemStatistics('ITP1_1_A')

      getRecentJudgeResultList()
      problemStatisticsPresenter.getOfficialProblemCategory()
    }
    initAppState()

    // Left side panel auto scroll
    const leftSideFrameElement = ref<HTMLDivElement>()
    const scrollTopLeftPanel = () => {
      if (leftSideFrameElement.value != undefined) {
        leftSideFrameElement.value.scrollTo(0, 0)
      }
    }

    // Right side panel auto scroll
    const rightSideFrameElement = ref<HTMLDivElement>()
    const scrollTopRightPanel = () => {
      if (rightSideFrameElement.value != undefined) {
        rightSideFrameElement.value.scrollTo(0, 0)
      }
    }

    // 下端までスクロールして情報更新
    let lock = false
    const handleScroll = () => {
      if (!leftSideFrameElement.value) return
      const { scrollTop, scrollHeight, clientHeight } = leftSideFrameElement.value
      if (scrollTop + clientHeight >= scrollHeight - 1) {
        if (lock) return
        lock = true
        try {
          if (frameStatePresenter.leftSideFrame.value == 'ModelAnswer') {
            // 模範解答リストの更新
            if (modelAnswerPageStatePresenter.isNotMoreModelAnswerList.value) return
            const newPageIndex = modelAnswerPageStatePresenter.pageIndex.value + 1
            addModelAnswerList(problemId.value, configStatePresenter.programLanguage.value, newPageIndex)
            modelAnswerPageStatePresenter.setPageIndex(newPageIndex)
          } else if (frameStatePresenter.leftSideFrame.value == 'History') {
            // 提出履歴の更新
            if (historyPageStatePresenter.isNotMoreHistoryList.value) return
            const newPageIndex = historyPageStatePresenter.pageIndex.value + 1
            addHistoryList(authStatePresenter.user.value.id, problemId.value, newPageIndex)
            historyPageStatePresenter.setPageIndex(newPageIndex)
          }
        } finally {
          setTimeout(() => {
            lock = false
          }, 500)
        }
      }
    }

    onBeforeMount(async () => {
      // セッションチェック
      await authStatePresenter.checkSession()
    })

    onMounted(() => {
      if (leftSideFrameElement.value) {
        leftSideFrameElement.value.addEventListener('scroll', handleScroll)
      }
      window.addEventListener('mousemove', editorPageStatePresenter.resizeTestFrameHeight)
      window.addEventListener('mouseup', editorPageStatePresenter.mouseUpEditorBottomSlideBar)
      window.addEventListener('resize', configStatePresenter.updateWindowSize)
      window.addEventListener('mousemove', frameStatePresenter.resizeLeftSideFrameWidth)
      window.addEventListener('mouseup', frameStatePresenter.mouseUpSlideBar)
      window.addEventListener('resize', frameStatePresenter.resizeMaxLeftSideFrameWidth)
      document.addEventListener('keydown', hotKeys)
    })

    onUnmounted(() => {
      if (leftSideFrameElement.value) {
        leftSideFrameElement.value.removeEventListener('scroll', handleScroll)
      }
      window.removeEventListener('mousemove', editorPageStatePresenter.resizeTestFrameHeight)
      window.removeEventListener('mouseup', editorPageStatePresenter.mouseUpEditorBottomSlideBar)
      window.removeEventListener('resize', configStatePresenter.updateWindowSize)
      window.removeEventListener('mousemove', frameStatePresenter.resizeLeftSideFrameWidth)
      window.removeEventListener('mouseup', frameStatePresenter.mouseUpSlideBar)
      window.removeEventListener('resize', frameStatePresenter.resizeMaxLeftSideFrameWidth)
      document.removeEventListener('keydown', hotKeys)
    })

    const leftAreaStyle = computed(() => {
      const width = `${frameStatePresenter.leftSideFrameWidth.value}px`
      const heightValue = introPresenter.isExitIntro.value
        ? configStatePresenter.windowHeight.value - 70
        : configStatePresenter.windowHeight.value - 80
      const height = `${heightValue}px`
      return `width: ${width}; height: ${height}`
    })

    // watchs
    watch(
      () => problemId.value,
      newProblemId => {
        // 問題が変わったら、各種情報を初期化
        changeProblem(newProblemId)
        modalStatePresenter.setIsShowProblemSearchModal(false)
        modalStatePresenter.setIsShowDashboardModal(false)
        modalStatePresenter.setIsShowArenaModal(false)
        frameStatePresenter.initFrameState()
        reviewPostPageStatePresenter.setIsFirstOpendPage(true)
        setReviewIsFirstOpendPage(true)
        modelAnswerPageStatePresenter.setIsFirstOpendPage(true)
      }
    )

    watch(
      () => authStatePresenter.isLogin.value,
      () => {
        if (authStatePresenter.isLogin.value) {
          // ログインしたとき
          bookmarkPresenter.getBookmark(authStatePresenter.user.value.id)
          notificationPresenter.loginNotification(authStatePresenter.user.value.id)
          modalStatePresenter.setIsShowLoginModal(false)
          getProblemDescription(configStatePresenter.displayLanguage.value, problemId.value)

          modelAnswerPageStatePresenter.setIsFirstOpendPage(true)

          if (!introPresenter.isExitIntro.value) {
            setTimeout(() => {
              introPresenter.startTour()
            }, 1000)
          }
        } else {
          // ログアウトしたとき
          notificationPresenter.logoutNotification()
        }
      }
    )

    watch(
      () => configStatePresenter.windowHeight.value,
      () => {
        // windowのサイズが変わったら、テストフレームの最大高を再計算
        editorPageStatePresenter.updateMaxTestFrameHeight(configStatePresenter.windowHeight.value / 2)
      }
    )

    watch(
      () => isJudgeLoading.value,
      () => {
        if (!isJudgeLoading.value) {
          if (judgeDetail.value && judgeDetail.value.submissionRecord.status == 'ACCEPTED') {
            // ACの場合、紙吹雪を表示
            runConfetti()
          }
        }
      }
    )

    watch(
      () => editorPageStatePresenter.isPastingIOConsole.value,
      () => {
        if (editorPageStatePresenter.isPastingIOConsole.value) {
          // テストケース貼り付けアニメーションが再実行されないようにする
          setTimeout(() => {
            editorPageStatePresenter.setIsPastingIOConsole(false)
          }, 3000)
        }
      }
    )

    watch(
      () => configStatePresenter.programLanguage.value,
      () => {
        // 言語が変わったら、模範解答ページを初期化
        initState(problemId.value, configStatePresenter.programLanguage.value)
      }
    )

    // shortcutKey setting
    const shortcutKey: Ref<string>[] = [ref('Alt+Shift+X'), ref('Alt+X')]

    const escape = (text: string) => {
      // クッキーに入出力する際にエスケープする
      return escapeText(text)
    }

    var escapeText = (str: string) => {
      return str
        .replace(/\\/g, '\\\\')
        .replace(/'/g, "\\'")
        .replace(/"/g, '\\"')
        .replace(/\//g, '\\/')
        .replace(/</g, '\\x3c')
        .replace(/>/g, '\\x3e')
        .replace(/(0x0D)/g, '\r')
        .replace(/(0x0A)/g, '\n')
        .replace(/&/g, '&amp;')
    }

    onMounted(() => {
      // クッキーから設定を取得
      let cookies = document.cookie
      cookies = escape(cookies)
      let cookiesArray = cookies.split(';')

      if (cookies.includes('key')) {
        for (let c of cookiesArray) {
          let cArray = c.split('=')
          cArray[0] = cArray[0].replace(' ', '')
          cArray[1] = cArray[1].replace(' ', '')
          if (cArray[0] == 'key0') {
            shortcutKey[0].value = cArray[1]
          } else if (cArray[0] == 'key1') {
            shortcutKey[1].value = cArray[1]
          }
        }
      } else {
        document.cookie = 'key0=Alt+Shift+X; max-age=2147483647'
        document.cookie = 'key1=Alt+X; max-age=2147483647'
      }

      if (cookies.includes('darkmode')) {
        // ダークモードの設定を取得
        for (let c of cookiesArray) {
          let cArray = c.split('=')
          cArray[0] = cArray[0].replace(' ', '')
          cArray[1] = cArray[1].replace(' ', '')
          if (cArray[0] == 'darkmode') {
            const isDarkMode = cArray[1] == 'true' ? true : false
            if (isDarkMode) {
              configStatePresenter.setViewMode('DARK')
            } else {
              configStatePresenter.setViewMode('LIGHT')
            }
          }
        }
      } else {
        document.cookie = 'darkmode=false; max-age=2147483647'
      }

      if (cookies.includes('showIntro')) {
        // サイトツアーを表示するかどうか
        for (let c of cookiesArray) {
          let cArray = c.split('=')
          cArray[0] = cArray[0].replace(' ', '')
          cArray[1] = cArray[1].replace(' ', '')
          if (cArray[0] == 'showIntro') {
            introPresenter.setIsExitIntro(cArray[1] == 'true' ? true : false)
          }
        }
      } else {
        document.cookie = 'showIntro=false; max-age=2147483647'
      }

      notificationPresenter.welcomeNotification()
    })

    const keyRun = (index: number, event: KeyboardEvent) => {
      if (shortcutKey[index].value == 'Alt+Shift+X') {
        if (event.altKey && event.shiftKey && event.code == 'KeyX') {
          event.preventDefault()
          return true
        }
      } else if (shortcutKey[index].value == 'Alt+X') {
        if (event.altKey && event.code == 'KeyX') {
          event.preventDefault()
          return true
        }
      } else if (shortcutKey[index].value == 'Ctrl+F11') {
        if (event.ctrlKey && event.code == 'F11') {
          event.preventDefault()
          return true
        }
      } else if (shortcutKey[index].value == 'Ctrl+F10') {
        if (event.ctrlKey && event.code == 'F10') {
          event.preventDefault()
          return true
        }
      } else if (shortcutKey[index].value == 'Shift+F10') {
        if (event.shiftKey && event.code == 'F10') {
          event.preventDefault()
          return true
        }
      } else if (shortcutKey[index].value == 'Shift+F9') {
        if (event.shiftKey && event.code == 'F9') {
          event.preventDefault()
          return true
        }
      } else if (shortcutKey[index].value == 'Alt+Shift+F10') {
        if (event.altKey && event.shiftKey && event.code == 'F10') {
          event.preventDefault()
          return true
        }
      } else if (shortcutKey[index].value == 'Alt+Shift+F9') {
        if (event.altKey && event.shiftKey && event.code == 'F9') {
          event.preventDefault()
          return true
        }
      } else if (shortcutKey[index].value == 'Ctrl+F5') {
        if (event.ctrlKey && event.code == 'F5') {
          event.preventDefault()
          return true
        }
      }
      return false
    }

    const keyTest = () => {
      // 試すショートカットキー
      if (authStatePresenter.isLogin.value) {
        if (editorPageStatePresenter.isFreeSpaceMode.value) {
          postTestSubmission(
            problemId.value,
            configStatePresenter.programLanguage.value,
            editorPageStatePresenter.sourceCode.value,
            'freespace'
          )
        } else {
          postTestSubmission(
            problemId.value,
            configStatePresenter.programLanguage.value,
            editorPageStatePresenter.sourceCode.value,
            'default'
          )
        }
      } else {
        notificationPresenter.noLoginNotification()
      }
    }

    const keySubmmit = () => {
      // 提出ショートカットキー
      if (authStatePresenter.isLogin.value) {
        if (arenaPresenter.arenaId.value != '') {
          postArenaSubmission(
            problemId.value,
            arenaPresenter.arenaProblemId.value,
            arenaPresenter.arenaId.value,
            configStatePresenter.programLanguage.value,
            editorPageStatePresenter.sourceCode.value
          )
        } else {
          postSubmission(
            problemId.value,
            configStatePresenter.programLanguage.value,
            editorPageStatePresenter.sourceCode.value
          )
        }
      } else {
        notificationPresenter.noLoginNotification()
      }
    }

    const hotKeys = (event: KeyboardEvent) => {
      // ショートカットキーをリスナーに登録
      if (keyRun(0, event)) {
        if (!modalStatePresenter.isShowFreeSpaceModal.value) {
          keyTest()
        }
      } else if (keyRun(1, event)) {
        if (!modalStatePresenter.isShowFreeSpaceModal.value) {
          keySubmmit()
        }
      }
    }

    onMounted(async () => {
      // TODO クエリーをエスケイプする

      // クエリーパラメータを処理
      const urlParams = new URLSearchParams(window.location.search)
      const problemId = urlParams.get('problemId')
      const arenaId = urlParams.get('arenaId')
      const userId = urlParams.get('userId')
      const courseName = urlParams.get('courseName')

      // ダッシュボード
      if (userId) {
        const res = await dashboardModalStatePresenter.isExistUser(userId)
        if (res != null) {
          // 指定されたユーザーが存在する場合はダッシュボードを表示
          dashboardModalStatePresenter.setInitialDashboardUser(res)
          modalStatePresenter.setIsShowDashboardModal(true)
        }
        if (courseName) {
          // コースも指定されている場合は、コースのタブを表示
          const courseList = computed(() => Object.values(coursePresenter.courseList.value))
          for (let i = 0; i < courseList.value.length; i++) {
            if (courseName == courseList.value[i].shortName) {
              dashboardModalStatePresenter.setCourseSerial(courseList.value[i].serial - 1)
              dashboardModalStatePresenter.setTabState('COURSE')
              break
            }
          }
        }
      }

      // アリーナ
      if (arenaId && problemId) {
        await arenaPresenter.getArenaDetailData(arenaId, authStatePresenter.user.value.id)
        let arenaProblemId = ''
        let arenaProblemIdIndex = -1
        let flag = false

        for (let i = 0; i < arenaPresenter.arenaProblemElementList.value.length; i++) {
          // アリーナに問題IDが存在するか
          if (arenaPresenter.arenaProblemElementList.value[i].problemId == problemId) {
            arenaProblemId = arenaPresenter.arenaProblemElementList.value[i].id
            arenaProblemIdIndex = arenaPresenter.arenaProblemElementList.value[i].serial
            flag = true
            break
          }
        }
        if (flag) {
          arenaPresenter.setArenaProblem(problemId, arenaId, arenaProblemId, arenaProblemIdIndex)
        }
      } else if (problemId) {
        // 問題を指定
        setProblemId(problemId)
      }
    })

    return {
      authStatePresenter,
      configStatePresenter,
      modalStatePresenter,
      frameStatePresenter,
      editorPageStatePresenter,
      Super,
      leftAreaStyle,
      leftSideFrameElement,
      rightSideFrameElement,
      scrollTopLeftPanel,
      scrollTopRightPanel,
      keyTest,
      keySubmmit,
      nested: {
        shortcutKey
      }
    }
  }
})
</script>

<style lang="postcss">
/* Applies globally. */
@import '~material-icons/iconfont/material-icons.css';
@tailwind base;
@tailwind components;
@tailwind utilities;

html {
  font-size: 16px;
  height: 100vh;
  max-height: 100vh;
}

.list-round-first {
  border-top-left-radius: 1rem;
  border-top-right-radius: 1rem;
}

.list-round-last {
  border-bottom-left-radius: 1rem;
  border-bottom-right-radius: 1rem;
}

.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.3s ease-out;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateY(-20px);
  opacity: 0;
}

.shepherd-theme-custom {
  width: 400px;
  padding: 5px;
  outline: none;
  overflow-wrap: break-word;
}

.shepherd-cancel-icon:focus {
  outline: none;
}

.shepherd-margin-right {
  transform: translateX(-20px);
}

.shepherd-margin-bottom {
  transform: translateY(-20px);
}

.shepherd-text {
  margin: 10px;
}

.shepherd-arrow:before {
  background-color: white;
}

.bg-notif {
  background-color: #fff !important;
  color: #000 !important;
  border: rgb(156 163 175) solid 1px !important;
}

.bg-notif-dark {
  background-color: #1e1e1e !important;
  color: #333 !important;
  border: rgb(94 94 94) solid 1px !important;
}
.icon-success {
  color: rgb(13 148 136) !important;
  margin: auto 4px;
}

.icon-error {
  margin: auto 4px;
  color: rgb(185 28 28) !important;
}

.icon-info {
  margin: auto 4px;
  color: #909399 !important;
}

.icon-warning {
  margin: auto 4px;
  color: #e6a23c !important;
}

.tit {
  background-color: #fff !important;
  color: #000 !important;
  font-size: 20px;
  margin-bottom: 8px;
}

.tit-dark {
  background-color: #1e1e1e !important;
  color: #fff !important;
  font-size: 20px;
  margin-bottom: 8px;
}

.tit-success {
  background-color: #fff !important;
  color: #67c23a !important;
  font-size: 20px;
  margin-bottom: 8px;
}

.tit-success-dark {
  background-color: #1e1e1e !important;
  color: #67c23a !important;
  font-size: 20px;
  margin-bottom: 8px;
}

.tit-error {
  background-color: #fff !important;
  color: #f56c6c !important;
  font-size: 20px;
  margin-bottom: 8px;
}

.tit-error-dark {
  background-color: #1e1e1e !important;
  color: #f56c6c !important;
  font-size: 20px;
  margin-bottom: 8px;
}

.tit-info {
  background-color: #fff !important;
  color: #909399 !important;
  font-size: 20px;
  margin-bottom: 8px;
}

.tit-info-dark {
  background-color: #1e1e1e !important;
  color: #909399 !important;
  font-size: 20px;
  margin-bottom: 8px;
}

.tit-warning {
  background-color: #fff !important;
  color: #e6a23c !important;
  font-size: 20px;
  margin-bottom: 8px;
}

.tit-warning-dark {
  background-color: #1e1e1e !important;
  color: #e6a23c !important;
  font-size: 20px;
  margin-bottom: 8px;
}

.msg {
  background-color: #fff !important;
  color: #000 !important;
}

.msg-dark {
  background-color: #1e1e1e !important;
  color: #dfdfdf !important;
}

.slide-cursor {
  cursor: col-resize;
}

.queue-area {
  grid-area: judges;
}
.not-select-display {
  user-select: none;
}
.darkScrollBar {
  color-scheme: dark;
}

.failed {
  animation: slide-out-down 1s 2s forwards;
}

@keyframes slide-out-down {
  from {
    transform: translateY(0);
    opacity: 1;
  }
  to {
    transform: translateY(100%);
    opacity: 0;
  }
}

.ok {
  animation: slide-out-up 2s 2s forwards;
}

@keyframes slide-out-up {
  from {
    transform: translateY(0);
    opacity: 1;
  }
  to {
    transform: translateY(-1000%);
    opacity: 0;
  }
}

.el-notification:hover {
  animation: none !important;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 8px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: #ffffff;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #ccc;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #aaa;
}

.custom-scrollbar-dark::-webkit-scrollbar {
  width: 8px;
}

.custom-scrollbar-dark::-webkit-scrollbar-track {
  background: #1a1a1a;
}

.custom-scrollbar-dark::-webkit-scrollbar-thumb {
  background: #666666;
}

.custom-scrollbar-dark::-webkit-scrollbar-thumb:hover {
  background: #444444;
}
</style>

<style lang="postcss" scoped>
.header-area {
  grid-area: header;
}

.footer-area {
  grid-area: footer;
}

.editor-area {
  grid-area: editor;
}

.sidemenu-area-left {
  grid-area: sidemenuleft;
}
.sidemenu-area-right {
  grid-area: sidemenuright;
}

.scrollbar {
  grid-area: scrollbar;
}

.problem-area {
  grid-area: problem;
}

.problem-area :deep(.menu) > ul {
  width: 100%;
}

.problem-area :deep(.menu) li {
  width: 50%;
  text-align: center;
}

.problem-area :deep(.menu) .tab-item {
  width: 100%;
}

.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.3s ease-out;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateY(-20px);
  opacity: 0;
}

.modal-size {
  width: 90vw;
  height: 70vh;
}

.modal-size-min {
  width: 50vw;
  height: 70vh;
}
</style>

<style>
/* リストをグローバルで整える */
ol {
  list-style: decimal;
  margin-left: 1.25rem; /* 約20px */
}
</style>
